import * as React from 'react';

import { Theme, styled } from '@mui/material/styles';

import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Editor from '../../../../core/Editor';
import SearchTermResultItem from '../../../../../api/model/SearchTermResultItem';
import { AssetSearchSource } from '../../../../core/EditorConstants';
import SearchProvider from './SearchProvider';
import { WithTranslation, withTranslation } from 'react-i18next';
import theme from 'src/common/ui/theme';
import { getBackgroundLightColor } from '../ImageEditorDialog';
import SearchInput from './SearchInput';

export const getPadding = (theme: Theme, number: number | number[]) => ({
  padding: Array.isArray(number) ? theme.spacing.apply(null, number) : theme.spacing(number),
});

export const getMarginRight = (theme: Theme, number: number | number[]) => ({
  marginRight: theme.spacing(1),
});


const styles = {
  dialogContent: {
    padding: '3px 3px 3px 2px'
  },
  dialogTitle: {
    ...getPadding(theme, [7, 0, 0])
  },
  dialog: {
    justifyContent: 'flex-start' as 'flex-start',
    alignItems: 'flex-start' as React.CSSProperties['alignItems'],
  },
  iconActionClose: {
    width: '35px',
    height: '35px',
    margin: '15px',
    ...getBackgroundLightColor(theme),
    padding: '0',
    position: 'absolute' as 'absolute',
    right: 0,
    top: 0
  },
  paperDialog: {
    marginLeft: 2,
    marginTop: 2,
    borderRadius: 4,
    }
};

const CustomDialog = styled(Dialog)(() => ({
  '.MuiDialog-scrollPaper': styles.dialog,
  '.MuiDialog-paper': styles.paperDialog,
}));


interface Props {
  editor: Editor,
  handleCloseDialog: () => void;
  handleSearchSelection: (searchItem: SearchTermResultItem) => void;
  assetSearchSources: AssetSearchSource[];
  defaultSearch: string;
  isSearchDialogOpen: boolean;
}

interface State {
  search: string; // The contents of the search text input
  loading: boolean; // Are we currently loading
  query: string; // The current search query
}


type Styled = any

type PropsWithStyles = Props & WithTranslation & Styled;

class SearchDialog extends React.Component<PropsWithStyles, State> {

  constructor(props: PropsWithStyles) {
    super(props);
    this.state = {
      search: this.props.defaultSearch,
      loading: false,
      query: this.props.defaultSearch
    }
  }

  handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      const value: string = event.target.value

      this.setState({ query: value });
    }
  }

  handleChange = (event) => {
    const value = event.target.value;
    this.setState({ search: value });
  }

  search = () => {
    this.setState({ query: this.state.search });
  }

  handleSearchSelection = (searchItem: SearchTermResultItem, source: AssetSearchSource) => {
    if (this.isVideoSearchDialog()) {
      this.props.editor.logAmplitudeEvent("VIDEO_SEARCH_DOWNLOADED_HTML5", { query: this.state.search, url: searchItem.url, source: source });
      this.props.editor.logGAEvent("VIDEO_SEARCH_DOWNLOADED_HTML5", null, false);
    } else {
      this.props.editor.logAmplitudeEvent("IMAGE_SEARCH_DOWNLOADED_HTML5", { query: this.state.search, url: searchItem.url, source: source });
      this.props.editor.logGAEvent("IMAGE_SEARCH_DOWNLOADED_HTML5", null, false);

    }
    this.props.handleSearchSelection(searchItem);
  }

  private isVideoSearchDialog = () => {
    return (this.props.assetSearchSources.indexOf(AssetSearchSource.PIXABAY_VIDEO) !== -1) || (this.props.assetSearchSources.indexOf(AssetSearchSource.PEXELS_VIDEO) !== -1)
  }



   componentDidMount() {
    //render first default search
     this.setState({ search: this.props.defaultSearch, query: this.props.defaultSearch });
  } 

  componentDidUpdate(props: PropsWithStyles) {
    if (this.props.defaultSearch !== props.defaultSearch) {
      this.setState({ search: this.props.defaultSearch, query: this.props.defaultSearch });
    }
  }

  render() {
    const { t } = this.props;
    return (
      <CustomDialog
        open={this.props.isSearchDialogOpen}
        onClose={this.props.handleCloseDialog}
        fullWidth={true}
        maxWidth={'xs'}
        scroll='paper'
        BackdropProps={{
          invisible: true
        }}        
      >
        <DialogTitle
          style={{ ...styles.dialogTitle }}
        >
          <React.Fragment>
            <IconButton
              onClick={this.props.handleCloseDialog}
              style={styles.iconActionClose}
              cyid="closeSearchDialogButton"
            >
              <Close color="action" />
            </IconButton>
          </React.Fragment>       

          <SearchInput
            value={this.state.search}
            onChange={this.handleChange}
            onKeyPress={this.handleKeyPress}
            onSearchClick={this.search}
            placeholder={t("leftbar.commons.search.placeholder-images")}
            ariaLabel={t("leftbar.commons.search.placeholder")}
            cyid='queryInput'
          />

        </DialogTitle>
        <DialogContent
          style={{ ...styles.dialogContent }}
        >
          {this.props.assetSearchSources.map((assetSearchSource: AssetSearchSource) => {
            return (
              <SearchProvider
                key={assetSearchSource}
                editor={this.props.editor}
                handleSearchSelection={this.handleSearchSelection}
                assetSearchSource={assetSearchSource}
                query={this.state.query}
              />
            );
          })}
        </DialogContent>
      </CustomDialog>
    );
  }
}

export default withTranslation("leftbar")(SearchDialog);

